import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { requestLaunchSample } from '../../util/api';

import Modal from '../Modal/Modal';

import css from './LaunchSampleModal.module.css';

/**
 * "Request a sample" on a launch page.
 *
 * Built on the BrandRepModal skeleton (Modal + local state + a thank-you
 * state) rather than Final Form: this is six fields and one required tick, and
 * the validation that matters — consent — is enforced server-side anyway
 * (POST /api/launch-sample-request 400s without it).
 *
 * CONSENT IS A DELIBERATE BLOCKER, not a pre-ticked formality. The record is
 * handed to a supplier and carries a real person's name, firm and contact
 * details, so the submit button stays disabled until it is ticked. There is no
 * "by submitting you agree" wording anywhere — implied consent is not consent.
 *
 * QUANTITY: the server's schema (launch_sample_requests) has no quantity
 * column, and adding one for a field the operator reads as prose would be
 * carrying a column for nothing. It is prefixed onto `notes` instead, which is
 * exactly how the operator reads it in the Airtable inbox.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onManageDisableScrolling
 * @param {string} props.launchId
 * @param {string} [props.launchTitle]
 * @param {string} [props.brandName]
 * @param {Object} [props.currentUser] - prefills name / firm / email
 * @param {Function} [props.onSubmitted] - fired once, after a stored request
 *   (this is where the caller records its engagement event)
 */
const LaunchSampleModal = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    launchId,
    launchTitle,
    brandName,
    currentUser,
    onSubmitted,
  } = props;
  const intl = useIntl();

  const profile = currentUser?.attributes?.profile;
  const identity = {
    // Prefill from the account, never silently submit it: the person still
    // sees and can correct every value before it goes to a supplier.
    name: profile?.displayName || [profile?.firstName, profile?.lastName].filter(Boolean).join(' '),
    // privateData.company_name is where this marketplace keeps the firm (see
    // selectFirmIdentity in sharingPreferences.duck). Readable here because
    // this is the visitor's OWN currentUser entity.
    firm: profile?.privateData?.company_name || '',
    email: currentUser?.attributes?.email || '',
  };

  const [values, setValues] = useState({
    name: identity.name || '',
    firm: identity.firm || '',
    email: identity.email || '',
    phone: '',
    project: '',
    location: '',
    quantity: '',
    notes: '',
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const setValue = (key, value) => setValues(prev => ({ ...prev, [key]: value }));

  const hasEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim());
  const canSubmit = !!values.name.trim() && hasEmail && consent && !submitting;

  const handleClose = () => {
    // Reset so the next launch starts clean — but keep the prefilled identity,
    // which is still true.
    setSubmitted(false);
    setError(null);
    setConsent(false);
    setValues(prev => ({ ...prev, phone: '', project: '', location: '', quantity: '', notes: '' }));
    onClose();
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const quantity = values.quantity.trim();
    const notes = values.notes.trim();
    const combinedNotes = [quantity ? `Quantity: ${quantity}` : null, notes]
      .filter(Boolean)
      .join(' — ');

    requestLaunchSample({
      launchId,
      launchTitle,
      name: values.name.trim(),
      firm: values.firm.trim(),
      email: values.email.trim().toLowerCase(),
      phone: values.phone.trim(),
      project: values.project.trim(),
      location: values.location.trim(),
      notes: combinedNotes,
      consent: true,
    })
      .then(res => {
        setSubmitting(false);
        // The endpoint answers honestly: `stored: false` means no warehouse
        // took the request, and telling someone their request is with the
        // supplier when it isn't would be the one unforgivable lie on this
        // form.
        if (!res || res.stored !== true) {
          setError('unstored');
          return;
        }
        setSubmitted(true);
        onSubmitted && onSubmitted();
      })
      .catch(() => {
        setSubmitting(false);
        setError('failed');
      });
  };

  const field = (key, labelId, extra = {}) => (
    <label className={css.field}>
      <span className={css.fieldLabel}>
        <FormattedMessage id={labelId} />
        {extra.required ? <span className={css.required}>*</span> : null}
      </span>
      <input
        className={css.input}
        type={extra.type || 'text'}
        value={values[key]}
        onChange={e => setValue(key, e.target.value)}
        placeholder={
          extra.placeholderId ? intl.formatMessage({ id: extra.placeholderId }) : undefined
        }
      />
    </label>
  );

  return (
    <Modal
      id="LaunchSampleModal"
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={handleClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
    >
      {submitted ? (
        <div className={css.thanks}>
          <div className={css.thanksMark} aria-hidden="true">
            ✓
          </div>
          <h2 className={css.thanksTitle}>
            <FormattedMessage id="LaunchSampleModal.thanksTitle" />
          </h2>
          <p className={css.thanksBody}>
            <FormattedMessage
              id="LaunchSampleModal.thanksBody"
              values={{
                brand: brandName || intl.formatMessage({ id: 'LaunchSampleModal.theSupplier' }),
              }}
            />
          </p>
          <button type="button" className={css.primaryButton} onClick={handleClose}>
            <FormattedMessage id="LaunchSampleModal.thanksClose" />
          </button>
        </div>
      ) : (
        <form className={css.body} onSubmit={handleSubmit}>
          <h2 className={css.title}>
            <FormattedMessage id="LaunchSampleModal.title" />
          </h2>
          <p className={css.intro}>
            <FormattedMessage
              id="LaunchSampleModal.intro"
              values={{
                launch: launchTitle || intl.formatMessage({ id: 'LaunchSampleModal.thisLaunch' }),
              }}
            />
          </p>

          <div className={css.grid}>
            {field('name', 'LaunchSampleModal.labelName', { required: true })}
            {field('firm', 'LaunchSampleModal.labelFirm')}
            {field('email', 'LaunchSampleModal.labelEmail', { required: true, type: 'email' })}
            {field('phone', 'LaunchSampleModal.labelPhone', { type: 'tel' })}
            {field('project', 'LaunchSampleModal.labelProject', {
              placeholderId: 'LaunchSampleModal.placeholderProject',
            })}
            {field('location', 'LaunchSampleModal.labelLocation', {
              placeholderId: 'LaunchSampleModal.placeholderLocation',
            })}
            {field('quantity', 'LaunchSampleModal.labelQuantity', {
              placeholderId: 'LaunchSampleModal.placeholderQuantity',
            })}
          </div>

          <label className={css.field}>
            <span className={css.fieldLabel}>
              <FormattedMessage id="LaunchSampleModal.labelNotes" />
            </span>
            <textarea
              className={css.textarea}
              rows={3}
              maxLength={1000}
              value={values.notes}
              onChange={e => setValue('notes', e.target.value)}
              placeholder={intl.formatMessage({ id: 'LaunchSampleModal.placeholderNotes' })}
            />
          </label>

          <label className={css.consentRow}>
            <input
              className={css.checkbox}
              type="checkbox"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
            />
            <span className={css.consentText}>
              <FormattedMessage
                id="LaunchSampleModal.consent"
                values={{
                  brand: brandName || intl.formatMessage({ id: 'LaunchSampleModal.theSupplier' }),
                }}
              />
            </span>
          </label>

          {error ? (
            <p className={css.error}>
              <FormattedMessage
                id={
                  error === 'unstored'
                    ? 'LaunchSampleModal.errorNotStored'
                    : 'LaunchSampleModal.errorFailed'
                }
              />
            </p>
          ) : null}

          <button
            type="submit"
            className={classNames(css.primaryButton, { [css.primaryButtonDisabled]: !canSubmit })}
            disabled={!canSubmit}
          >
            <FormattedMessage
              id={submitting ? 'LaunchSampleModal.submitting' : 'LaunchSampleModal.submit'}
            />
          </button>
          <p className={css.footnote}>
            <FormattedMessage id="LaunchSampleModal.footnote" />
          </p>
        </form>
      )}
    </Modal>
  );
};

export default LaunchSampleModal;
